import {makeAutoObservable, reaction} from 'mobx';

import type {AccountApiModel} from '../../../api/account';
import type {CoreUserAccountsListParams} from '../../../api/types/core/methods';
import type {
  Destructible,
  WithBusyManager,
  WithReactionDispose,
} from '../../../utils/store';

import {accountStore} from '../../../api/account';
import {createCollectionStore} from '../../../api/utils/collectionStore';
import {alert} from '../../../components/alert';
import {accountDelete} from '../../../services/entity-action/account/account.delete';
import {accountMakePrimary} from '../../../services/entity-action/account/account.make-primary';
import {isCancelCommand} from '../../../services/entity-action/utils';
import {logger} from '../../../utils/logger';
import {AsyncBusyManager, ReactionDisposeManager} from '../../../utils/store';

type ReasonKey = 'delete' | 'fetch' | 'markPrimary';

const MAX_ACCOUNTS = 10;

export class AccountsStore
  implements Destructible, WithBusyManager<ReasonKey>, WithReactionDispose
{
  readonly busy = new AsyncBusyManager<ReasonKey>();
  disposer = new ReactionDisposeManager();
  list = createCollectionStore<AccountApiModel, CoreUserAccountsListParams>({
    method: 'user.accounts.list',
    params: {
      limit: MAX_ACCOUNTS,
      query: [['type', '!=', 'external_id']],
    },
  });

  constructor() {
    this.busy.observe(this, {
      delete: 'delete',
      fetch: 'fetch',
      markPrimary: 'markPrimary',
    });

    makeAutoObservable(this, {}, {autoBind: true});

    this.disposer.watch(
      'create',
      reaction(
        () => accountStore.creating,
        creating => {
          if (creating.success && creating.entity) {
            this.list.addItem(creating.entity);
          }
        },
      ),
    );
  }

  destruct() {
    this.disposer.dispose();

    this.list.clear();
  }

  get isAbleToAddAccount() {
    return this.list.items.length < MAX_ACCOUNTS;
  }

  canAccountBeDeleted(account: AccountApiModel) {
    return (
      this.list.items.filter(acc => acc.type === 'email').length > 1 ||
      account.type === 'phone'
    );
  }

  async delete(account: AccountApiModel) {
    try {
      const result = await accountDelete(account);

      if (isCancelCommand(result)) {
        return;
      }

      this.list.removeItem(account);

      this.fetch(true);
    } catch (error) {
      alert.error(error);

      logger.error(error);
    }
  }

  async fetch(silent = false) {
    try {
      await this.list.fetch({}, {silent});
    } catch (error) {
      logger.error(error);
    }
  }

  async markPrimary(account: AccountApiModel) {
    try {
      const result = await accountMakePrimary(account);

      if (isCancelCommand(result)) {
        return;
      }

      this.list.items
        .slice()
        .filter(acc => acc.type === result.type)
        .forEach(acc => {
          this.list.updateItem(acc, {
            primary: acc._id === result._id,
          });
        });
    } catch (error) {
      alert.error(error);

      logger.error(error);
    }
  }
}
